"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, type MouseEvent } from "react";
import {
  CREATOR,
  FIGMA_PLUGIN_URL,
  PARENT_COMPANY,
  SUPPORT_EMAIL,
} from "../constants";
import { RunFirstCta } from "./run-first-cta";
import { FooterAccount, NavAccount } from "./nav-account";
import { ThemeToggle } from "./theme-toggle";

const CURRENT_YEAR = new Date().getFullYear();

const NAV_ITEMS: Array<{ href: string; label: string; match: (p: string) => boolean }> = [
  { href: "/about", label: "About", match: (p) => p === "/about" || p.startsWith("/about/") },
  { href: "/pricing", label: "Pricing", match: (p) => p === "/pricing" || p.startsWith("/pricing/") },
  { href: "/blogs", label: "Blog", match: (p) => p === "/blogs" || p.startsWith("/blogs/") },
  { href: "/contact", label: "Contact", match: (p) => p === "/contact" || p.startsWith("/contact/") },
];

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname() ?? "/";
  const router = useRouter();

  const handleMobileNavClick = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    setMenuOpen(false);
    router.push(href);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <header className={`site-header${menuOpen ? " nav-open" : ""}`}>
      <nav className="site-nav" aria-label="Main navigation">
        <Link className="brand-lockup" href="/#top" aria-label="Snap Site home" onClick={() => setMenuOpen(false)}>
          <LogoMark priority />
          <LogoWordmark priority />
        </Link>
        <div className="nav-links">
          {NAV_ITEMS.map((item) => {
            const active = item.match(pathname);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={active ? "is-active" : undefined}
                aria-current={active ? "page" : undefined}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
        <div className="nav-actions">
          <ThemeToggle />
          <NavAccount />
          <RunFirstCta
            className="nav-cta"
            surface="header"
            guestLabel="Start a free audit"
            authedLabel="+ New audit"
          />
        </div>
        <button
          type="button"
          className="nav-menu-btn"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="primary-mobile-menu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
      </nav>
      <div className="nav-mobile-menu" id="primary-mobile-menu" hidden={!menuOpen}>
        {NAV_ITEMS.map((item) => {
          const active = item.match(pathname);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={active ? "is-active" : undefined}
              aria-current={active ? "page" : undefined}
              onClick={(event) => handleMobileNavClick(event, item.href)}
            >
              {item.label}
            </Link>
          );
        })}
        <div className="nav-mobile-actions">
          <ThemeToggle />
          <NavAccount />
        </div>
        <RunFirstCta
          className="nav-cta nav-mobile-cta"
          surface="mobile-header"
          guestLabel="Start a free audit"
          authedLabel="+ New audit"
          onActivate={() => setMenuOpen(false)}
        />
      </div>
    </header>
  );
}

export function SiteFooter() {
  const pathname = usePathname() ?? "/";
  const isActive = (href: string) =>
    href === "/"
      ? pathname === "/"
      : pathname === href || pathname.startsWith(href + "/");

  return (
    <footer className="site-footer">
      <div className="footer-shell">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link className="brand-lockup" href="/#top" aria-label="Snap Site home">
              <LogoMark small />
              <LogoWordmark small />
            </Link>
            <p>
              Website UX audits in your browser — pinned findings,
              responsive captures, and a Figma plugin when you need it.
            </p>
          </div>

          <div className="footer-links" aria-label="Footer navigation">
            <div>
              <b>Product</b>
              <FooterLink href="/about" isActive={isActive("/about")}>About</FooterLink>
              <FooterLink href="/pricing" isActive={isActive("/pricing")}>Pricing</FooterLink>
              <a href={FIGMA_PLUGIN_URL} target="_blank" rel="noopener noreferrer">Figma plugin</a>
              <FooterLink href="/updates" isActive={isActive("/updates")}>Roadmap</FooterLink>
            </div>
            <div>
              <b>Resources</b>
              <FooterLink href="/ux-audit-tool" isActive={isActive("/ux-audit-tool")}>UX audit tool</FooterLink>
              <FooterLink href="/accessibility-audit" isActive={isActive("/accessibility-audit")}>Accessibility audit</FooterLink>
              <FooterLink href="/landing-page-audit" isActive={isActive("/landing-page-audit")}>Landing page audit</FooterLink>
              <FooterLink href="/website-audit-tool" isActive={isActive("/website-audit-tool")}>Website audit tool</FooterLink>
              <FooterLink href="/figma-ux-audit-plugin" isActive={isActive("/figma-ux-audit-plugin")}>Figma UX audit plugin</FooterLink>
              <FooterLink href="/website-annotation-tool" isActive={isActive("/website-annotation-tool")}>Website annotation tool</FooterLink>
              <FooterLink href="/blogs" isActive={isActive("/blogs")}>Blog</FooterLink>
              <Link href="/#faq">FAQ</Link>
              <FooterLink href="/sitemap" isActive={isActive("/sitemap")}>Sitemap</FooterLink>
            </div>
            <div>
              <b>Legal</b>
              <FooterLink href="/privacy" isActive={isActive("/privacy")}>Privacy</FooterLink>
              <FooterLink href="/terms" isActive={isActive("/terms")}>Terms</FooterLink>
            </div>
            <div>
              <b>Get in touch</b>
              <a href="/contact#contact-form">Contact us</a>
              <a className="footer-email-link" href={`mailto:${SUPPORT_EMAIL}`}>
                {/* Address kept whole (white-space: nowrap); the wider last
                    column keeps it on one line. */}
                <span>{SUPPORT_EMAIL}</span>
              </a>
              <FooterAccount />
            </div>
          </div>
        </div>

        <div className="footer-base">
          <span className="footer-copyright">
            Copyright {CURRENT_YEAR} Snap Site. All rights reserved.
          </span>
          <span className="footer-credits">
            A collaboration between{" "}
            <span>{CREATOR.name}</span>{" "}
            and{" "}
            <a
              href={PARENT_COMPANY.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {PARENT_COMPANY.name}
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({
  href,
  isActive,
  children,
}: {
  href: string;
  isActive: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className={isActive ? "is-active" : undefined}
      aria-current={isActive ? "page" : undefined}
    >
      {children}
    </Link>
  );
}

export function FigmaIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="figma-icon"
    >
      <path
        fill="currentColor"
        d="M6.75,15 C5.839,14.316 5.25,13.227 5.25,12 C5.25,10.773 5.839,9.684 6.75,9 C5.839,8.316 5.25,7.227 5.25,6 C5.25,3.929 6.929,2.25 9,2.25 L15,2.25 C17.071,2.25 18.75,3.929 18.75,6 C18.75,7.227 18.161,8.316 17.25,9 C18.161,9.684 18.75,10.773 18.75,12 C18.75,14.071 17.071,15.75 15,15.75 C14.156,15.75 13.377,15.471 12.75,15 L12.75,18 C12.75,20.071 11.071,21.75 9,21.75 C6.929,21.75 5.25,20.071 5.25,18 C5.25,16.773 5.839,15.684 6.75,15 Z M11.25,3.75 L9,3.75 C7.757,3.75 6.75,4.757 6.75,6 C6.75,7.243 7.757,8.25 9,8.25 L11.25,8.25 Z M11.25,9.75 L9,9.75 C7.757,9.75 6.75,10.757 6.75,12 C6.75,13.243 7.757,14.25 9,14.25 L11.25,14.25 L11.25,9.75 Z M12.75,3.75 L12.75,8.25 L15,8.25 C16.243,8.25 17.25,7.243 17.25,6 C17.25,4.757 16.243,3.75 15,3.75 Z M9,15.75 C7.757,15.75 6.75,16.757 6.75,18 C6.75,19.243 7.757,20.25 9,20.25 C10.243,20.25 11.25,19.243 11.25,18 L11.25,15.75 Z M15,9.75 C13.757,9.75 12.75,10.757 12.75,12 C12.75,13.243 13.757,14.25 15,14.25 C16.243,14.25 17.25,13.243 17.25,12 C17.25,10.757 16.243,9.75 15,9.75 Z"
      />
    </svg>
  );
}

// `priority` on the header instances: without it, next/image lazy-loads
// both brand SVGs and every cold visit renders a brand-less header for the
// first seconds — on a ~24-second average marketing visit that's a real
// trust cost. Footer instances stay lazy (below the fold).
export function LogoMark({
  small = false,
  priority = false,
}: {
  small?: boolean;
  priority?: boolean;
}) {
  return (
    <Image
      className={small ? "logo-mark logo-mark-small" : "logo-mark"}
      src="/brand/snap-site-mark.svg"
      alt=""
      width={32}
      height={32}
      aria-hidden="true"
      draggable={false}
      priority={priority}
    />
  );
}

// Brand wordmark — replaces the plain "Snap Site" text in the header /
// footer lockup with the proper rendered logotype. The image is purely
// decorative because the parent <Link> already exposes
// `aria-label="Snap Site home"`, so screen readers get a single, clean
// announcement instead of "Snap Site Snap Site home".
export function LogoWordmark({
  small = false,
  priority = false,
}: {
  small?: boolean;
  priority?: boolean;
}) {
  return (
    <Image
      className={
        small ? "logo-wordmark logo-wordmark-small" : "logo-wordmark"
      }
      src="/brand/logo-wordmark.svg"
      alt=""
      width={674}
      height={204}
      aria-hidden="true"
      draggable={false}
      priority={priority}
    />
  );
}
